import type { ReactNode } from 'react';
import {
  ChatThreadHeader,
  type IChatThreadHeaderBack,
  type IChatThreadHeaderLabels,
} from '@/shared/components';

import { useRainThreadActions } from '../hooks/useRainThreadActions';
import type { IRainThreadListAdapter } from '../hooks/scope';

interface IRainChatThreadHeaderProps {
  /** Thread list + mutations for the surface's scope and agent. */
  adapter: IRainThreadListAdapter;
  /**
   * Currently selected thread id (controlled by the parent). Undefined while
   * the surface's first thread is still being created — a draft's chat now
   * renders before its thread exists.
   */
  selectedThreadId: string | undefined;
  /** Bubble up a pick or a freshly-created thread id to the parent. */
  onSelectThread: (threadId: string) => void;
  /**
   * The back button and its surface-specific label. Omit it when the page
   * header already has a back button.
   */
  back?: IChatThreadHeaderBack;
  /** Additional actions shown beside the thread controls. */
  actions?: ReactNode;
  /** Which side the thread cluster sits on. Defaults to `end`. */
  align?: 'start' | 'end';
  /** Header copy. */
  labels: IChatThreadHeaderLabels;
}

/**
 * Binds a Rain thread list to the chat column's header row. Same data as
 * `RainThreadSelector`, in the 56px header shape the draft editor uses.
 *
 * The row itself renders unconditionally: it carries the back button, so
 * swapping the whole thing for a skeleton while the thread list fetches would
 * take away the only way out of the editor. The name alone goes to a skeleton
 * for that window, since `options` is empty until the fetch lands.
 */
export function RainChatThreadHeader({
  adapter,
  selectedThreadId,
  onSelectThread,
  back,
  actions,
  align,
  labels,
}: IRainChatThreadHeaderProps) {
  const { refetch, isCreating, isLoading } = adapter;
  const { options, createNewThread, renameSelectedThread } =
    useRainThreadActions({ adapter, selectedThreadId, onSelectThread });

  return (
    <ChatThreadHeader
      options={options}
      selectedThreadId={selectedThreadId}
      onSelectThread={onSelectThread}
      // Fetch the latest threads lazily when the menu opens.
      onOpen={refetch}
      onCreateThread={createNewThread}
      onRenameThread={renameSelectedThread}
      back={back}
      actions={actions}
      align={align}
      isCreating={isCreating}
      isLoading={isLoading}
      labels={labels}
      dataTestId="rain-chat-thread-header"
    />
  );
}
